// @flow
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import React, { PureComponent } from 'react';

import CardJob from 'components/fragments/card/CardJob';
import Pagination from 'components/fragments/Pagination';
import Loading from 'components/fragments/Loading';
import JobFilter, { getFiltersOnQuery } from 'components/job/JobFilter';
import { STATUS_DEFAULT, STATUS_LOADING } from 'constants/statusConstants';
import { type RouterProps } from 'types/Router';
import type { JobListType } from 'types/JobList';
import type { JobFiltersType, JobFiltersSearchType } from 'types/JobFilter';
import type { Status } from 'types/Status';
import { dynamicClassName } from 'utils/dynamicClassName';

export type StateProps = {
  jobs: Array<JobListType>,
  jobFilters: JobFiltersType,
  maxPage: number,
  totalJobs: number,
  status: Status
};

export type DispatchProps = {
  initialFetchJobList: (filter?: JobFiltersSearchType) => void,
  fetchJobListPreview: (filter: JobFiltersSearchType) => void
};

type Props = DispatchProps & StateProps & RouterProps;

// https://www.codementor.io/blog/react-optimization-5wiwjnf9hj#2-functionstateless-components-and-reactpurecomponent
class JobList extends PureComponent<Props> {
  componentDidMount() {
    this.props.initialFetchJobList(
      getFiltersOnQuery(queryString.parse(this.props.location.search), this.props.jobFilters)
    );
  }

  _onFilterChange = (filters: JobFiltersSearchType) => {
    this.props.fetchJobListPreview({ ...filters, page: 1 });
  };

  renderCards = () => {
    const { jobs } = this.props;
    const restJobs = jobs.length % 3;

    return jobs.map((job, index) => {
      const isOnLastLine =
        (restJobs === 0 && index >= jobs.length - 3) || (restJobs !== 0 && index >= jobs.length - restJobs);

      const classNameContainer = dynamicClassName('col col-md-6 col-lg-4 mb-2 mb-lg-3');
      isOnLastLine ? classNameContainer.add('mb-lg-0') : classNameContainer.add('mb-lg-4');

      return (
        <div key={index} className={classNameContainer.build()}>
          <CardJob
            link={`${job.link}`}
            title={job.title}
            city={job.city}
            club={job.structure.name}
            embleme={job.structure.embleme}
            type_contrat={job.type_contrat}
            jobtype={job.jobtype}
          />
        </div>
      );
    });
  };

  _getPaginationOption = () => {
    let {
      maxPage,
      location: { pathname, search }
    } = this.props;

    const resultOption = [];
    search = search ? search.replace(/page=\d+/gi, '') : '?';

    for (let pageIndex = 1; pageIndex <= maxPage; pageIndex++) {
      resultOption.push({
        number: pageIndex,
        url: `${pathname}${search}page=${pageIndex}`
      });
    }

    return resultOption;
  };

  render() {
    const {
      jobFilters,
      maxPage,
      totalJobs,
      jobs,
      match: {
        params: { page }
      },
      status
    } = this.props;

    if (status === STATUS_DEFAULT || status === STATUS_LOADING) {
      return <Loading isWhite />;
    }

    return (
      <section>
        <JobFilter jobFilters={jobFilters} onFilterChange={this._onFilterChange} />

        <div className="header-filter__sep mb-5" />
        <div className="row">
          <div className="col col-lg-8on9">
            <p className="ft-h6 ft-up ft-black-secondary ft-right">
              {totalJobs > 1 ? `${totalJobs} offres disponibles` : `${totalJobs} offre disponible`}
            </p>
            <h2 className="ft-h3 mb-3">Toutes nos offres</h2>
          </div>
        </div>

        {jobs && jobs.length > 0 ? (
          <div className="row">
            <div className="col">
              <div className="row">{this.renderCards()}</div>
            </div>
          </div>
        ) : (
          <div className="placeholder col col-lg-8on9">
            <p className="ft-h5">
              <i className="icon icon-articles-empty"></i>
              <br />
              Aucune offre correspondante trouvée.
            </p>
          </div>
        )}

        {parseInt(maxPage, 10) > 1 && (
          <Pagination
            pages={this._getPaginationOption()}
            currentPage={parseInt(page, 10) || 1}
            lastPage={parseInt(maxPage, 10)}
          />
        )}
      </section>
    );
  }
}

export default withRouter(JobList);
